import { isGgufFormat } from "./hardware.ts";

// ── Commande de déploiement ─────────────────────────────────────────────────

export const DEPLOY_ENGINES = [
  { id: "vllm", label: "vLLM" },
  { id: "sglang", label: "SGLang" },
  { id: "llamacpp", label: "llama.cpp" },
  { id: "trtllm", label: "TensorRT-LLM" },
] as const;
export type DeployEngine = (typeof DEPLOY_ENGINES)[number]["id"];

/** Part de la mémoire telle que le moteur la calcule : 0.90, ou trois décimales quand elle en a (SGLang : 0.835). */
const fraction = (x: number) => (Math.round(x * 1000) % 10 ? x.toFixed(3) : x.toFixed(2));

export type DeployConfig = {
  repo: string; weightFormat: string; nativeLabel?: string; kvFormat: string;
  context: number; sequences: number; tp: number; pp: number; utilization: number;
  /** llama.cpp : dépôt GGUF publié et ses quantifications (llama-server le télécharge avec -hf). */
  ggufRepo?: string;
  ggufQuants?: string[];
  /** llama.cpp : déchargement vers la RAM (voir offloadPlan), à la place de -ngl 99. */
  offloadFlag?: string;
  /** Contexte étendu par YaRN (ModelPreset.yarn) : au-delà de `native`, chaque moteur l'active par une option. */
  yarn?: { factor: number; original: number; native: number };
  /** Dépôt publié seulement au format de Mistral (params.json, consolidated-*.safetensors) : vLLM le lit avec ses options
   *  « mistral » (README de Mistral Large 3) ; les autres moteurs veulent un config.json. */
  mistralFormat?: boolean;
};

const GGUF_QUANT: Record<string, string> = {
  bf16: "BF16", fp32: "F32", fp8: "Q8_0", int8: "Q8_0", q8_0: "Q8_0", q6_k: "Q6_K", q5_k_m: "Q5_K_M",
  q4_k_m: "Q4_K_M", q3_k_m: "Q3_K_M", q2_k: "Q2_K", iq2_xxs: "IQ2_XXS", nvfp4: "Q4_K_M", mxfp4: "MXFP4", int4: "Q4_K_M",
};

/** Fichier GGUF de llama.cpp pour ce format : son nom de quantification, le dépôt publié qui l'a (sinon undefined).
 *  IQ2_XXS : souvent publié seulement dans sa version dynamique d'Unsloth (UD-IQ2_XXS), comme ggufSize. */
function ggufFile(c: Pick<DeployConfig, "weightFormat" | "nativeLabel" | "ggufRepo" | "ggufQuants">) {
  const quant = c.weightFormat === "native"
    ? (/MXFP4/.test(c.nativeLabel ?? "") ? "MXFP4" : /FP8/.test(c.nativeLabel ?? "") ? "Q8_0" : "Q4_K_M")
    : GGUF_QUANT[c.weightFormat] ?? "Q4_K_M";
  const ud = c.weightFormat === "iq2_xxs" && !c.ggufQuants?.includes(quant) && c.ggufQuants?.includes(`UD-${quant}`);
  const file = ud ? `UD-${quant}` : quant;
  return { file, hf: c.ggufRepo && c.ggufQuants?.includes(file) ? c.ggufRepo : undefined, a: /^I/.test(file) ? "an" : "a" };
}

/** PlayStation 5 : PS5LM Plus lance llama.cpp lui-même (son planificateur choisit le contexte et le cache), d'où
 *  des étapes plutôt qu'une commande. Prérequis et chemins : README de github.com/abhi245y/PS5LM (octobre 2026). */
export function ps5Steps(c: Pick<DeployConfig, "repo" | "weightFormat" | "nativeLabel" | "ggufRepo" | "ggufQuants">): string {
  const { file, hf, a } = ggufFile(c);
  return [
    "# PlayStation 5: PS5LM Plus runs llama.cpp on the console's GPU (github.com/abhi245y/PS5LM)",
    "# Needs a jailbroken PS5 (firmware 7.00 to 13.60) with kstuff-lite, etaHEN and ShadowMountPlus loaded",
    "# 1. Download the GGUF file on your computer",
    hf ? `hf download ${hf} --include "*${file}.gguf" "*${file}/*" --local-dir ps5-models` : `# Find ${a} ${file} GGUF build of ${c.repo || "this model"} on Hugging Face`,
    "# 2. Copy it to a USB drive: faster than /data/PS5LM/models, where the console writes at 1 to 2 MB/s",
    "# 3. Pick it in PS5LM Plus's model library: it sets the context and the KV cache itself",
    "# Chat and OpenAI-compatible API: http://<your-ps5-ip>:8081",
  ].join("\n");
}

/** Commande de lancement pour la configuration choisie (lignes « # » = explications). */
export function deployCommand(engine: DeployEngine, c: DeployConfig): string {
  const gpus = c.tp * c.pp;
  const nodes = Math.ceil(gpus / 8);
  const repo = c.repo || "<your-org>/<your-model>";
  const notes: string[] = [];
  const args: string[] = [];
  const preQuant = ["int8", "nvfp4", "mxfp4", "int4"].includes(c.weightFormat);
  // YaRN au-delà du contexte de config.json (options documentées par Qwen pour vLLM, SGLang et llama.cpp).
  const yarn = c.yarn && c.context > c.yarn.native ? c.yarn : undefined;
  const rope = yarn && `{"rope_type":"yarn","factor":${yarn.factor.toFixed(1)},"original_max_position_embeddings":${yarn.original}}`;
  if (yarn) notes.push(`# Beyond ${yarn.native.toLocaleString("en-US")} tokens this model needs YaRN rope scaling (factor ${yarn.factor}): ${engine === "trtllm" ? "set rope_scaling in its config.json first" : "enabled below"}`);
  const gguf = isGgufFormat(c.weightFormat);
  const fmt = c.weightFormat.toUpperCase().replace("_", " ");

  if (engine === "llamacpp") {
    const { file, hf, a } = ggufFile(c);
    if (!hf) notes.push(`# Download ${a} ${file} GGUF build of ${repo} from Hugging Face`);
    if (gpus > 1) notes.push(`# llama.cpp spreads the layers over all ${gpus} visible GPUs`);
    if (nodes > 1) notes.push("# Several machines: see llama.cpp RPC (rpc-server)");
    if (c.offloadFlag) notes.push(`# Part of the weights stays in system RAM (${c.offloadFlag}): slower, but it fits`);
    // -c est le contexte TOTAL, partagé entre les --parallel requêtes.
    args.push(hf ? `-hf ${hf}:${file}` : `-m ${repo.split("/").pop()}-${file}.gguf`,
      `-c ${c.context * c.sequences}`, `--parallel ${c.sequences}`, c.offloadFlag?.startsWith("-ngl") ? c.offloadFlag : "-ngl 99", "-fa on");
    if (c.offloadFlag?.startsWith("--n-cpu-moe")) args.push(c.offloadFlag);
    if (yarn) args.push(`--rope-scaling yarn --rope-scale ${yarn.factor} --yarn-orig-ctx ${yarn.original}`);
    if (c.kvFormat === "q8_0" || c.kvFormat === "fp8") args.push("-ctk q8_0 -ctv q8_0");
    if (c.kvFormat === "q4_0") args.push("-ctk q4_0 -ctv q4_0");
    if (c.kvFormat === "fp8") notes.push("# llama.cpp has no FP8 cache: Q8_0 is the closest");
    return [...notes, `llama-server ${args.join(" \\\n  ")}`].join("\n");
  }

  if (gguf) notes.push(`# ${fmt} is a GGUF format: llama.cpp runs it best. Showing the closest setup.`);
  if (c.mistralFormat && engine !== "vllm") notes.push("# Published in Mistral's own format only (params.json, no config.json): vLLM reads it as is, this engine needs a converted checkpoint");
  if (preQuant) notes.push(`# ${fmt}: point to a pre-quantized ${fmt} checkpoint of this model${engine === "trtllm" ? " (NVIDIA Model Optimizer)" : " (the engine reads its quantization_config)"}`);
  if (["q8_0", "q4_0"].includes(c.kvFormat)) notes.push(`# ${c.kvFormat.toUpperCase()} KV cache is llama.cpp only: FP8 is the closest`);
  const kvFp8 = c.kvFormat !== "bf16";

  if (engine === "vllm") {
    if (nodes > 1) notes.push(`# ${nodes} nodes: start a Ray cluster first (ray start --head on one node, ray start --address=<head>:6379 on the others)`);
    args.push(`--tensor-parallel-size ${c.tp}`);
    if (c.pp > 1) args.push(`--pipeline-parallel-size ${c.pp}`);
    args.push(`--max-model-len ${c.context}`, `--max-num-seqs ${c.sequences}`, `--gpu-memory-utilization ${fraction(c.utilization)}`);
    if (c.mistralFormat) args.push("--tokenizer-mode mistral --config-format mistral --load-format mistral");
    if (c.weightFormat === "bf16") args.push("--dtype bfloat16");
    if (c.weightFormat === "fp8") args.push("--quantization fp8");
    if (kvFp8) args.push("--kv-cache-dtype fp8");
    if (rope) args.push(`--hf-overrides '{"rope_scaling":${rope}}'`);
    return [...notes, `vllm serve ${repo} \\\n  ${args.join(" \\\n  ")}`].join("\n");
  }
  if (engine === "sglang") {
    if (nodes > 1) notes.push(`# ${nodes} nodes: add --nnodes ${nodes} --node-rank <i> --dist-init-addr <head-ip>:5000 on each node`);
    args.push(`--tp-size ${c.tp}`);
    if (c.pp > 1) args.push(`--pp-size ${c.pp}`);
    args.push(`--context-length ${c.context}`, `--max-running-requests ${c.sequences}`, `--mem-fraction-static ${fraction(c.utilization)}`);
    if (c.weightFormat === "fp8") args.push("--quantization fp8");
    if (kvFp8) args.push("--kv-cache-dtype fp8_e4m3");
    if (rope) args.push(`--json-model-override-args '{"rope_scaling":${rope}}'`);
    return [...notes, `python -m sglang.launch_server --model-path ${repo} \\\n  ${args.join(" \\\n  ")}`].join("\n");
  }
  // TensorRT-LLM
  if (c.weightFormat === "fp8") notes.push("# FP8: use an FP8 checkpoint made with NVIDIA Model Optimizer for best speed");
  if (nodes > 1) notes.push(`# ${nodes} nodes: launch with mpirun or Slurm across the nodes`);
  args.push(`--tp_size ${c.tp}`, `--pp_size ${c.pp}`, `--max_seq_len ${c.context}`, `--max_batch_size ${c.sequences}`, `--kv_cache_free_gpu_memory_fraction ${fraction(c.utilization)}`);
  return [...notes, `trtllm-serve ${repo} \\\n  ${args.join(" \\\n  ")}`].join("\n");
}
